import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { showToast } from "./Toast";

export default function CartDrawer({ openAuthModal, setActivePage }) {
  const { cart, removeFromCart, updateQty, clearCart, cartSubtotal, cartOpen, setCartOpen } = useCart();
  const { isLoggedIn, token } = useAuth();

  const handleCheckout = async () => {
    if (!isLoggedIn()) { openAuthModal("login"); return; }
    if (cart.length === 0) { showToast("Cart khali hai!"); return; }

    // Group by restaurant — take first item's restaurantId
    const restaurantId = cart[0]?.restaurantId;
    if (!restaurantId) { showToast("Restaurant ID missing"); return; }

    try {
      // Add items to backend cart first
      for (const item of cart) {
        await fetch("https://api.shubhamraj01.in/api/cart-item/add", {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ foodId: item.id, quantity: item.qty, ingredients: [] }),
        });
      }
      setCartOpen(false);
      setActivePage("detail");
    } catch(e) {
      showToast("Checkout failed: " + e.message);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={() => setCartOpen(false)}
        style={{
          position: "fixed", inset: 0, background: "rgba(28,24,20,.45)",
          zIndex: 300, opacity: cartOpen ? 1 : 0,
          pointerEvents: cartOpen ? "all" : "none", transition: "opacity .3s",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "380px", maxWidth: "100vw",
        background: "var(--surface)", borderLeft: "1.5px solid var(--border)",
        zIndex: 301, display: "flex", flexDirection: "column",
        transform: cartOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform .35s cubic-bezier(.4,0,.2,1)",
      }}>
        {/* Head */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "1.25rem 1.5rem", borderBottom: "1.5px solid var(--border)",
        }}>
          <h3 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "1.1rem" }}>
            🛒 Your Cart
          </h3>
          <button
            onClick={() => setCartOpen(false)}
            style={{
              width: "34px", height: "34px", borderRadius: "10px",
              border: "1.5px solid var(--border)", background: "transparent",
              color: "var(--text2)", cursor: "pointer", fontSize: "1rem",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.5rem" }}>
          {cart.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "3rem 1rem", color: "var(--text3)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: ".75rem",
            }}>
              <span style={{ fontSize: "2.5rem", opacity: .4 }}>🛒</span>
              <p style={{ fontSize: ".9rem" }}>Your cart is empty</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} style={{
                display: "flex", alignItems: "center", gap: "1rem",
                padding: ".9rem 0", borderBottom: "1px solid var(--border)",
              }}>
                {/* Image */}
                <div style={{
                  width: "56px", height: "56px", borderRadius: "10px",
                  overflow: "hidden", background: "var(--surface2)", flexShrink: 0,
                }}>
                  {item.images?.[0]
                    ? <img src={item.images[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>🍱</div>
                  }
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".88rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize: ".8rem", color: "var(--primary)", fontWeight: 600, marginTop: ".1rem" }}>
                    ₹{item.price * item.qty}
                  </div>
                  {/* Qty controls */}
                  <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginTop: ".4rem" }}>
                    <button onClick={() => updateQty(item.id, item.qty - 1)} style={{ width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "var(--surface2)", cursor: "pointer", fontSize: ".9rem" }}>−</button>
                    <span style={{ fontSize: ".85rem", fontWeight: 600, minWidth: "16px", textAlign: "center" }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1)} style={{ width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "var(--surface2)", cursor: "pointer", fontSize: ".9rem" }}>+</button>
                  </div>
                </div>

                {/* Remove */}
                <button
                  onClick={() => removeFromCart(item.id)}
                  style={{
                    width: "28px", height: "28px", borderRadius: "8px",
                    border: "1.5px solid var(--border)", background: "transparent",
                    color: "var(--text3)", cursor: "pointer", fontSize: ".9rem",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >✕</button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div style={{ padding: "1.25rem 1.5rem", borderTop: "1.5px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".9rem", marginBottom: ".4rem", color: "var(--text2)" }}>
              <span>Subtotal</span><span>₹{cartSubtotal}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".9rem", marginBottom: ".75rem", color: "var(--text2)" }}>
              <span>Delivery</span><span>₹40</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: "1.05rem", marginBottom: "1rem" }}>
              <span>Total</span><span>₹{cartSubtotal + 40}</span>
            </div>
            <button
              onClick={handleCheckout}
              style={{
                width: "100%", background: "var(--primary)", color: "#fff",
                border: "none", padding: ".9rem", borderRadius: "12px",
                fontWeight: 700, fontSize: ".95rem", cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Proceed to Checkout →
            </button>
          </div>
        )}
      </div>
    </>
  );
}